<template>
    <AppLayout title="Revisi Penyusunan Dokumen Pengadaan">
        <template #header>
            <BreadCrumb :breadcrumb="$page.props.bc"/>
        </template>
        
        <div class="py-4">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
                <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg">
                    <section class="text-gray-600 body-font">
                        <div class="container px-5 py-5 mx-auto">
                            <div class="flex flex-wrap w-full mb-5 flex-col">
                                <!-- Header Info -->
                                <div class="mb-6 p-4 bg-gray-50 rounded-lg">
                                    <div class="flex justify-between items-center">
                                        <div>
                                            <h3 class="text-lg font-medium text-gray-900">
                                                Revisi: {{ dataViewDokumenPengadaan?.paket_pengadaan?.nama_paket_pengadaan }}
                                            </h3>
                                            <p class="mt-1 text-sm text-gray-600">
                                                UUID: {{ dataViewDokumenPengadaan?.uuid }}
                                            </p>
                                        </div>
                                        <Button
                                            label="Kembali"
                                            icon="pi pi-arrow-left"
                                            class="p-button-secondary"
                                            @click="goBack"
                                        />
                                    </div>
                                </div>

                                <!-- Form -->
                                <form @submit.prevent="submitRevision">
                                    <Accordion :multiple="true" :activeIndex="[0, 1, 2]">
                                        <!-- File Uploads Section -->
                                        <AccordionTab>
                                            <template #header>
                                                <span class="flex align-items-center gap-2 w-full">
                                                    <span class="font-bold white-space-nowrap">File Dokumen Pengadaan</span>
                                                    <span class="text-red-500 text-sm ml-2">*Revisi</span>
                                                </span>
                                            </template>
                                            
                                            <div class="space-y-4">
                                                <Message severity="info" :closable="false">
                                                    <p>Upload file baru untuk mengganti file yang sudah ada. File lama akan dihapus secara otomatis.</p>
                                                </Message>
                                                
                                                <!-- Current Files Display -->
                                                <div v-if="dataViewDokumenPengadaan?.dokumen_pengadaan_file?.length > 0" class="mb-4">
                                                    <h4 class="font-medium text-gray-900 mb-3">File Saat Ini</h4>
                                                    <div class="space-y-2">
                                                        <div
                                                            v-for="file in dataViewDokumenPengadaan.dokumen_pengadaan_file"
                                                            :key="file.id"
                                                            class="flex items-center justify-between p-2 bg-gray-50 rounded border"
                                                        >
                                                            <div class="flex items-center space-x-2">
                                                                <i class="pi pi-file text-blue-500"></i>
                                                                <span class="text-sm text-gray-900">{{ file.nama_file }}</span>
                                                                <span class="text-xs text-gray-500">
                                                                    ({{ file.file_dokumen_type?.nama || 'Unknown type' }})
                                                                </span>
                                                            </div>
                                                            <div class="text-xs text-gray-500">
                                                                {{ formatDate(file.created_at) }}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <!-- File Upload Component -->
                                                <FileInput :file_uploads="form.file_uploads" :exlude_hps="true" :required="false" />
                                            </div>
                                        </AccordionTab>

                                        <!-- HPS Section -->
                                        <AccordionTab>
                                            <template #header>
                                                <span class="flex align-items-center gap-2 w-full">
                                                    <span class="font-bold white-space-nowrap">HPS</span>
                                                    <span v-if="!isHpsValid" class="text-red-500 text-sm ml-2">
                                                        (Data HPS tidak valid)
                                                    </span>
                                                    <span v-else class="text-red-500 text-sm ml-2">*Revisi</span>
                                                </span>
                                            </template>
                                            
                                            <div class="space-y-4">
                                                <Message severity="warn" :closable="false">
                                                    <p>Upload file HPS baru akan mengganti seluruh data HPS yang ada. Data lama akan disimpan dalam riwayat.</p>
                                                </Message>

                                                <!-- Current HPS Display -->
                                                <div v-if="dataViewDokumenPengadaan?.detail_hps?.length > 0" class="mb-4">
                                                    <h4 class="font-medium text-gray-900 mb-3">HPS Saat Ini</h4>
                                                    <div class="overflow-x-auto">
                                                        <table class="min-w-full divide-y divide-gray-200">
                                                            <thead class="bg-gray-50">
                                                                <tr>
                                                                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Uraian</th>
                                                                    <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Volume</th>
                                                                    <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Satuan</th>
                                                                    <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Harga Satuan</th>
                                                                    <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody class="bg-white divide-y divide-gray-200">
                                                                <tr v-for="item in dataViewDokumenPengadaan.detail_hps" :key="item.id">
                                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ item.uraian }}</td>
                                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">{{ item.volume }}</td>
                                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">{{ item.satuan }}</td>
                                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{{ formatCurrency(item.harga_satuan) }}</td>
                                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{{ formatCurrency(item.volume * item.harga_satuan) }}</td>
                                                                </tr>
                                                            </tbody>
                                                            <tfoot class="bg-gray-50">
                                                                <tr>
                                                                    <td colspan="4" class="px-6 py-3 text-right text-sm font-medium text-gray-900">Total HPS:</td>
                                                                    <td class="px-6 py-3 text-right text-sm font-medium text-gray-900">{{ formatCurrency(dataViewDokumenPengadaan.total_hps) }}</td>
                                                                </tr>
                                                            </tfoot>
                                                        </table>
                                                    </div>
                                                </div>

                                                <!-- HPS Input Component -->
                                                <FileHpsInput
                                                    :file_hps="form.hps.file_hps"
                                                    @update:file_hps="handleFileHpsUpdate"
                                                    @update:detail_hps="handleDetailHpsUpdate"
                                                    @validation-changed="updateHpsValidation"
                                                    :pagu_anggaran="dataViewDokumenPengadaan?.paket_pengadaan?.pagu_anggaran || 0"
                                                />
                                                <div v-if="hpsError" class="text-red-500 text-sm mt-2">{{ hpsError }}</div>
                                            </div>
                                        </AccordionTab>

                                        <!-- Confirmation Section -->
                                        <AccordionTab>
                                            <template #header>
                                                <span class="flex align-items-center gap-2 w-full">
                                                    <span class="font-bold white-space-nowrap">Konfirmasi Revisi</span>
                                                    <span class="text-red-500 text-sm ml-2">*Revisi</span>
                                                </span>
                                            </template>
                                            
                                            <div class="space-y-4">
                                                <Message severity="warn" :closable="false">
                                                    <p><strong>Peringatan:</strong> Revisi akan mengubah data dokumen pengadaan. Pastikan semua data sudah benar sebelum melanjutkan.</p>
                                                </Message>

                                                <div class="bg-gray-50 p-4 rounded-lg">
                                                    <h4 class="font-medium text-gray-900 mb-3">Ringkasan Perubahan</h4>
                                                    <ul class="space-y-2 text-sm text-gray-600">
                                                        <li v-for="change in getChangeSummary()" :key="change">• {{ change }}</li>
                                                        <li v-if="getChangeSummary().length === 0" class="text-gray-400">Tidak ada perubahan yang akan diterapkan</li>
                                                    </ul>
                                                </div>

                                                <div class="flex items-center space-x-2">
                                                    <Checkbox v-model="confirmRevision" :binary="true" />
                                                    <label class="text-sm text-gray-700">
                                                        Saya memahami bahwa revisi ini akan mengubah data dokumen pengadaan dan tidak dapat dibatalkan
                                                    </label>
                                                </div>
                                            </div>
                                        </AccordionTab>
                                    </Accordion>

                                    <!-- Submit Button -->
                                    <div class="mt-6 flex justify-end space-x-3">
                                        <Button
                                            label="Batal"
                                            icon="pi pi-times"
                                            class="p-button-secondary"
                                            @click="goBack"
                                            :disabled="isSubmitting"
                                        />
                                        <!-- Batalkan Paket Button - Only show for PPK -->
                                        <Button
                                            v-if="isPPK"
                                            label="Batalkan Paket"
                                            icon="pi pi-ban"
                                            class="p-button-danger"
                                            @click="showCancelDialog = true"
                                            :disabled="isSubmitting"
                                        />
                                        <Button
                                            label="Simpan Revisi"
                                            icon="pi pi-check"
                                            class="p-button-primary"
                                            type="submit"
                                            :disabled="isSubmitDisabled"
                                            :loading="isSubmitting"
                                        />
                                    </div>
                                </form>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>

        <!-- Cancel Package Confirmation Dialog -->
        <Dialog 
            v-model:visible="showCancelDialog" 
            :style="{width: '450px'}" 
            header="Konfirmasi Pembatalan Paket" 
            :modal="true"
            :closable="false"
        >
            <div class="confirmation-content">
                <i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem; color: #ff6b35;" />
                <span>
                    <p class="mb-4">Apakah Anda yakin ingin membatalkan paket pengadaan ini?</p>
                    <p class="mb-4 text-sm text-gray-600">
                        Tindakan ini akan mengubah status paket menjadi <strong>"DITOLAK"</strong> dan tidak dapat dibatalkan.
                    </p>
                </span>
            </div>
            
            <div class="mt-4">
                <label for="alasan_penolakan" class="block text-sm font-medium text-gray-700 mb-2">
                    Alasan Pembatalan <span class="text-red-500">*</span>
                </label>
                <Textarea 
                    id="alasan_penolakan"
                    v-model="cancelReason" 
                    rows="4" 
                    cols="50" 
                    placeholder="Jelaskan alasan pembatalan paket pengadaan..."
                    class="w-full"
                    :class="{ 'p-invalid': cancelReasonError }"
                />
                <small v-if="cancelReasonError" class="p-error">{{ cancelReasonError }}</small>
            </div>
            
            <template #footer>
                <Button 
                    label="Batal" 
                    icon="pi pi-times" 
                    @click="closeCancelDialog" 
                    class="p-button-text" 
                    :disabled="isCancelling"
                />
                <Button 
                    label="Ya, Batalkan Paket" 
                    icon="pi pi-check" 
                    @click="cancelPackage" 
                    class="p-button-danger"
                    :loading="isCancelling"
                    :disabled="!cancelReason.trim()"
                />
            </template>
        </Dialog>
    </AppLayout>
</template>

<script>
import { defineComponent } from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Accordion from 'primevue/accordion'
import AccordionTab from 'primevue/accordiontab'
import Button from 'primevue/button'
import FileUpload from 'primevue/fileupload'
import Message from 'primevue/message'
import Checkbox from 'primevue/checkbox'
import Dialog from 'primevue/dialog'
import Textarea from 'primevue/textarea'
import { Inertia } from '@inertiajs/inertia'
import axios from 'axios'
import readXlsxFile from 'read-excel-file'
import BreadCrumb from '@/Components/BreadCrumb.vue'
import FileInput from './partials/FileInput.vue'
import FileHpsInput from './partials/FileHpsInput.vue'

export default defineComponent({
    name: 'RevisiPenyusunanDokumenPengadaanForm',
    components: {
        AppLayout,
        Accordion,
        AccordionTab,
        Button,
        FileUpload,
        Message,
        Checkbox,
        Dialog,
        Textarea,
        BreadCrumb,
        FileInput,
        FileHpsInput
    },
    props: {
        dataViewDokumenPengadaan: Object,
        file_input_pengadaan: Array,
        back_url_param: Object
    },
    data() {
        return {
            isSubmitting: false,
            confirmRevision: false,
            isHpsValid: true,
            hasHpsValidationIssue: false,
            hpsError: '',
            showCancelDialog: false,
            isCancelling: false,
            cancelReason: '',
            cancelReasonError: '',
            form: {
                uuid: this.dataViewDokumenPengadaan?.uuid,
                file_uploads: [],
                hps: {
                    file_hps: null,
                    detail_hps: []
                }
            }
        }
    },
    computed: {
        isPPK() {
            return this.$page.props.session_role?.role?.name?.toLowerCase() === 'ppk'
        },
        hasPendingInvalidHps() {
            return this.hasHpsValidationIssue
        },
        isSubmitDisabled() {
            return !this.confirmRevision || this.isSubmitting || !this.hasChanges() || this.hasPendingInvalidHps
        }
    },
    mounted() {
        this.initializeFileUploads()
    },
    methods: {
        goBack() {
            Inertia.visit(route('dokumen_pengadaan.list_revisi_penyusunan_dokumen_pengadaan'))
        },

        initializeFileUploads() {
            // Initialize file uploads structure similar to FormPenyusunan.vue
            if (this.file_input_pengadaan && this.file_input_pengadaan.length > 0) {
                this.form.file_uploads = this.file_input_pengadaan.map(fileType => ({
                    id_file_dokumen_pengadaan: null,
                    id_jadwal_pengadaan: null,
                    id_dokumen_pengadaan: this.dataViewDokumenPengadaan?.id,
                    id_file_dokumen_prosedur: fileType.id_file_dokumen_prosedur || fileType.id,
                    kode_file: fileType.kode_file,
                    kode_prosedur: fileType.kode_prosedur || 'penyusunan_dokumen_pengadaan_1f',
                    nama_file: '',
                    file_path: '',
                    file_type: '',
                    file_dokumen_type: (fileType.file_dokumen_type && fileType.file_dokumen_type.nama) ? fileType.file_dokumen_type : {
                        nama: fileType.nama || fileType.file_dokumen_type?.nama || fileType.kode_file.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
                        kode: fileType.kode_file || fileType.kode
                    },
                    file_dokumen_type_id: fileType.file_dokumen_type_id || null,
                    multiple: fileType.is_multiple === 1 || fileType.is_multiple === true,
                    is_required: fileType.is_required, // Include is_required field
                    new_file: []
                }))
            }
        },

        updateHpsValidation(isValid) {
            this.isHpsValid = isValid
            this.hasHpsValidationIssue = !isValid
        },
        handleFileHpsUpdate(file) {
            this.form.hps.file_hps = file
            if (file === null && this.form.hps.detail_hps.length === 0 && this.isHpsValid) {
                this.hasHpsValidationIssue = false
            }
        },
        handleDetailHpsUpdate(detailHps) {
            this.form.hps.detail_hps = detailHps
            if (detailHps.length === 0 && this.form.hps.file_hps === null && this.isHpsValid) {
                this.hasHpsValidationIssue = false
            }
        },

        hasChanges() {
            // Check if there are any file uploads
            const hasFileChanges = this.form.file_uploads.some(file => file.new_file && file.new_file.length > 0)
            
            // Check if there are HPS changes
            const hasHpsChanges = this.form.hps.file_hps !== null && this.form.hps.detail_hps.length > 0
            
            return hasFileChanges || hasHpsChanges
        },

        getChangeSummary() {
            const changes = []
            
            // Check file changes
            this.form.file_uploads.forEach(file => {
                if (file.new_file && file.new_file.length > 0) {
                    changes.push(`${file.file_dokumen_type.nama}: ${file.new_file.length} file baru akan mengganti file existing`)
                }
            })
            
            // Check HPS changes
            if (this.form.hps.file_hps !== null && this.form.hps.detail_hps.length > 0) {
                changes.push(`HPS: ${this.form.hps.detail_hps.length} item baru akan mengganti HPS existing`)
            }
            
            return changes
        },

        async submitRevision() {
            if (!this.confirmRevision) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Peringatan',
                    detail: 'Mohon centang konfirmasi revisi terlebih dahulu',
                    life: 3000
                })
                return
            }

            if (!this.hasChanges()) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Peringatan',
                    detail: 'Tidak ada perubahan yang akan diterapkan',
                    life: 3000
                })
                return
            }

            if (this.hasPendingInvalidHps) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Mohon perbaiki semua kesalahan validasi HPS sebelum menyimpan revisi',
                    life: 5000
                })
                return
            }

            // Validasi total HPS tidak boleh melebihi pagu
            const pagu = this.dataViewDokumenPengadaan?.paket_pengadaan?.pagu_anggaran || 0;
            const totalHps = (this.form.hps.detail_hps || []).reduce((sum, item) => sum + ((item.volume || 0) * (item.harga_satuan || 0)), 0);
            if (pagu > 0 && totalHps > pagu) {
                this.hpsError = `Total HPS (Rp ${this.formatCurrency(totalHps)}) tidak boleh melebihi Pagu Anggaran (Rp ${this.formatCurrency(pagu)})`;
                return;
            }
            this.hpsError = '';

            this.isSubmitting = true

            try {
                // Create FormData for file uploads
                const formData = new FormData()
                
                // Add basic form data
                formData.append('uuid', this.form.uuid)
                
                // Handle file uploads (regular files)
                this.form.file_uploads.forEach((file, index) => {
                    if (file.new_file && file.new_file.length > 0) {
                        file.new_file.forEach((newFile, fileIndex) => {
                            formData.append(`file_uploads[${index}][new_file][${fileIndex}]`, newFile)
                        })
                        formData.append(`file_uploads[${index}][id_file_dokumen_prosedur]`, file.id_file_dokumen_prosedur)
                        formData.append(`file_uploads[${index}][kode_file]`, file.kode_file)
                        formData.append(`file_uploads[${index}][kode_prosedur]`, file.kode_prosedur)
                    }
                })
                
                // Handle HPS file upload
                if (this.form.hps.file_hps) {
                    formData.append('hps[file_hps]', this.form.hps.file_hps)
                }
                
                // Handle HPS detail data
                if (this.form.hps.detail_hps && this.form.hps.detail_hps.length > 0) {
                    formData.append('hps[detail_hps]', JSON.stringify(this.form.hps.detail_hps))
                }

                await axios.post(route('dokumen_pengadaan.action_revisi_penyusunan_dokumen_pengadaan'), formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                })

                this.$toast.add({
                    severity: 'success',
                    summary: 'Berhasil',
                    detail: 'Revisi dokumen pengadaan berhasil disimpan',
                    life: 3000
                })

                // Redirect back to list
                setTimeout(() => {
                    this.goBack()
                }, 1500)

            } catch (error) {
                console.error('Error submitting revision:', error)
                
                let errorMessage = 'Terjadi kesalahan saat menyimpan revisi'
                if (error.response?.status === 403) {
                    errorMessage = 'Anda tidak memiliki akses untuk melakukan revisi ini'
                } else if (error.response?.status === 401) {
                    errorMessage = 'Sesi Anda telah berakhir. Silakan login ulang'
                } else if (error.response?.data?.message) {
                    errorMessage = error.response.data.message
                } else if (error.response?.data?.errors) {
                    // Handle validation errors
                    const errors = error.response.data.errors
                    const errorList = Object.values(errors).flat()
                    errorMessage = errorList.join(', ')
                }

                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: errorMessage,
                    life: 5000
                })
            } finally {
                this.isSubmitting = false
            }
        },

        formatCurrency(value) {
            return new Intl.NumberFormat('id-ID', { 
                style: 'currency', 
                currency: 'IDR',
                minimumFractionDigits: 0
            }).format(value)
        },

        formatDate(dateString) {
            if (!dateString) return ''
            return new Date(dateString).toLocaleDateString('id-ID', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })
        },

        closeCancelDialog() {
            this.showCancelDialog = false
            this.cancelReason = ''
            this.cancelReasonError = ''
        },

        async cancelPackage() {
            // Validate reason
            if (!this.cancelReason.trim()) {
                this.cancelReasonError = 'Alasan pembatalan wajib diisi'
                return
            }

            if (this.cancelReason.trim().length < 10) {
                this.cancelReasonError = 'Alasan pembatalan minimal 10 karakter'
                return
            }

            this.cancelReasonError = ''
            this.isCancelling = true

            try {
                await axios.post(route('dokumen_pengadaan.batalkan_paket'), {
                    uuid: this.form.uuid,
                    alasan_penolakan: this.cancelReason.trim()
                })

                this.$toast.add({
                    severity: 'success',
                    summary: 'Berhasil',
                    detail: 'Paket pengadaan berhasil dibatalkan',
                    life: 3000
                })

                // Close dialog and redirect
                this.closeCancelDialog()
                
                setTimeout(() => {
                    this.goBack()
                }, 1500)

            } catch (error) {
                console.error('Error canceling package:', error)
                
                let errorMessage = 'Terjadi kesalahan saat membatalkan paket'
                if (error.response?.status === 403) {
                    errorMessage = 'Anda tidak memiliki akses untuk membatalkan paket ini'
                } else if (error.response?.status === 401) {
                    errorMessage = 'Sesi Anda telah berakhir. Silakan login ulang'
                } else if (error.response?.status === 422) {
                    errorMessage = error.response.data?.message || 'Data tidak valid'
                } else if (error.response?.data?.message) {
                    errorMessage = error.response.data.message
                }

                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: errorMessage,
                    life: 5000
                })
            } finally {
                this.isCancelling = false
            }
        }
    }
})
</script>
